/**
 * Refactored AtencionDialog - Uses modular components and custom hook
 * Original: 1273 lines -> Refactored: ~120 lines
 * Backup saved at: AtencionDialog.original.js
 */
import React from 'react';
import { Dialog, DialogContent } from './ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import ActaDiariaDialog from './ActaDiariaDialog';

// Import modular components
import {
  useAtencion,
  usePrintAtencion,
  AtencionToolbar,
  AtencionRequerimientoInfo,
  AtencionTestigoForm,
  AtencionDatosForm,
  AtencionSolicitudesList,
  AtencionValidationAlert,
  ImagePreviewDialog
} from './Atencion';

const AtencionDialog = ({ requerimiento, open, onClose, onUpdate, readOnly = false }) => {
  const { user } = useAuth();
  
  // Use custom hook for all state and logic
  const {
    loading,
    sendingEmail,
    atencion,
    solicitudes,
    loadingSolicitudes,
    inspeccionActaEstado,
    platformConfig,
    actaInputRef,
    testigoTipo,
    testigo,
    datosAtencion,
    savedSoluciones,
    previewImage,
    showActaDiariaDialog,
    selectedSolicitudForActa,
    editingActaId,
    terminatingSolicitudId,
    setTestigo,
    setDatosAtencion,
    setPreviewImage,
    handleTestigoTipoChange,
    handleSave,
    handleActaUpload,
    handleActaDelete,
    handleTerminarSolicitud,
    handleOpenActaDiaria,
    handleOpenActaDiariaEdit,
    handleActaDiariaCreated,
    handleActaDiariaUpdated,
    closeActaDiariaDialog,
    updateSolicitudSolucion,
    handleSendEmail,
    handleSendWhatsApp,
    handleTerminar,
    fetchUltimoTestigo,
    magicLoading,
    sendingWhatsapp,
    allSolicitudesTerminado,
    canSendEmail,
    canPrint,
    canTerminar,
    npsHabilitado,
    sealData,
    sealLoading
  } = useAtencion(requerimiento, open);

  // Print hook
  const { handlePrint } = usePrintAtencion();

  // Wrapper handlers
  const onSave = () => handleSave(onUpdate);
  const onSendEmail = () => handleSendEmail(onUpdate);
  const onSendWhatsApp = () => handleSendWhatsApp(onUpdate);
  const onTerminar = () => handleTerminar(onUpdate, onClose);
  const onPrint = () => {
    if (sealLoading) return;
    const success = handlePrint(
      requerimiento,
      atencion,
      testigo,
      datosAtencion,
      solicitudes,
      platformConfig,
      user?.nombre,
      sealData
    );
    if (!success) {
      toast.error('No se pudo abrir la ventana de impresión. Verifique que no esté bloqueada.');
    }
  };

  // Loading state
  if (loading && !atencion) {
    return (
      <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent className="max-w-5xl" preventMobileClose>
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-[#9d8272] mx-auto"></div>
              <p className="mt-4 text-sm text-[#5A5A5A]">Cargando atención...</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <>
      <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
        <DialogContent 
          className="max-w-5xl w-[95vw] max-h-[95vh] overflow-y-auto p-0"
          data-testid="atencion-dialog"
          preventMobileClose
        >
          {/* Toolbar */}
          <AtencionToolbar
            codigo={atencion?.codigo || `A${requerimiento?.codigo}`}
            readOnly={readOnly}
            loading={loading}
            canSendEmail={canSendEmail}
            canPrint={canPrint}
            canTerminar={canTerminar}
            allSolicitudesTerminado={allSolicitudesTerminado}
            actaAtencionUrl={datosAtencion.actaAtencionUrl}
            actaEstado={datosAtencion.actaEstado}
            whatsappEnabled={platformConfig?.aprobar_acta_atencion_whatsapp}
            sendingEmail={sendingEmail}
            sendingWhatsapp={sendingWhatsapp}
            onSave={onSave}
            onSendEmail={onSendEmail}
            onSendWhatsApp={onSendWhatsApp}
            onPrint={onPrint}
            onTerminar={onTerminar}
            onClose={onClose}
            sealLoading={sealLoading}
          />

          <div className="p-4 sm:p-6 space-y-6">
            {/* 1. Información del Requerimiento */}
            <AtencionRequerimientoInfo requerimiento={requerimiento} />

            {/* 2. Datos del Testigo */}
            <AtencionTestigoForm
              testigoTipo={testigoTipo}
              testigo={testigo}
              onTipoChange={handleTestigoTipoChange}
              onTestigoChange={setTestigo}
              readOnly={readOnly}
              onMagicClick={fetchUltimoTestigo}
              magicLoading={magicLoading}
              magicTooltip="Copiar últimos datos: Testigo"
            />

            {/* 3. Datos de la Atención */}
            <AtencionDatosForm
              datosAtencion={datosAtencion}
              onDatosChange={setDatosAtencion}
              onActaUpload={handleActaUpload}
              onActaDelete={handleActaDelete}
              actaInputRef={actaInputRef}
              inspeccionId={atencion?.inspeccion_id}
              inspeccionActaEstado={inspeccionActaEstado}
              atencionId={atencion?.id}
              sendingEmail={sendingEmail}
              readOnly={readOnly}
            />

            {/* 4. Lista de Solicitudes */}
            <AtencionSolicitudesList
              solicitudes={solicitudes}
              loadingSolicitudes={loadingSolicitudes}
              savedSoluciones={savedSoluciones}
              onUpdateSolucion={updateSolicitudSolucion}
              onTerminarSolicitud={handleTerminarSolicitud}
              terminatingSolicitudId={terminatingSolicitudId}
              onOpenActaDiaria={handleOpenActaDiaria}
              onOpenActaDiariaEdit={handleOpenActaDiariaEdit}
              onPreviewImage={setPreviewImage}
              readOnly={readOnly}
              npsHabilitado={npsHabilitado}
            />

            {/* Validation Alert */}
            {!readOnly && (
              <AtencionValidationAlert
                allSolicitudesTerminado={allSolicitudesTerminado}
                canTerminar={canTerminar}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Acta Diaria Dialog */}
      {showActaDiariaDialog && selectedSolicitudForActa && atencion && (
        <ActaDiariaDialog
          atencionId={atencion.id}
          solicitudAtencion={selectedSolicitudForActa}
          requerimiento={requerimiento}
          atencion={atencion}
          actaId={editingActaId}
          platformConfig={platformConfig}
          open={showActaDiariaDialog}
          onClose={closeActaDiariaDialog}
          onCreated={handleActaDiariaCreated}
          onUpdated={handleActaDiariaUpdated}
        />
      )}

      {/* Image Preview Dialog */}
      <ImagePreviewDialog
        imageUrl={previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </>
  );
};

export default AtencionDialog;
